import React, { useEffect, useState } from "react";
import { Col, Divider, Form, Input, Row, Select, Tag, Typography } from "antd";
import { getFiserv, listFiservCards, upsertFiserv } from "./api";
import { FiservCardType, FiservConfig } from "./types";
import {
  CampoModo,
  CampoMontoMaximo,
  CamposComunes,
  GatewayPanel,
  GatewayPanelProps,
} from "./GatewayPanel";
import { FiservInstallments } from "./FiservInstallments";
import { Subtitulo } from "./ui";

const { Text } = Typography;

/**
 * Un juego de credenciales. El que corresponde al modo elegido lleva "En uso": los dos se
 * guardan, pero sólo uno cobra (xcons-docs#636).
 */
const Credenciales: React.FC<{
  titulo: string;
  produccion: boolean;
  prefix: "production" | "integration";
}> = ({ titulo, produccion, prefix }) => {
  const modo = Form.useWatch("productionModeEnabled");
  return (
    <>
      <Subtitulo
        extra={!!modo === produccion && <Tag color="green">En uso</Tag>}
      >
        {titulo}
      </Subtitulo>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Nombre de la tienda" name={`${prefix}StoreName`}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Clave secreta" name={`${prefix}SharedSecret`}>
            <Input.Password placeholder="••••••••" />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export const FiservPanel: React.FC<GatewayPanelProps> = ({
  sellerCode,
  ...panel
}) => {
  const [cards, setCards] = useState<FiservCardType[]>([]);

  useEffect(() => {
    if (!panel.open) return;
    listFiservCards(sellerCode)
      .then(setCards)
      .catch(() => undefined);
  }, [panel.open, sellerCode]);

  return (
    <GatewayPanel<FiservConfig>
      {...panel}
      // Más ancho que el resto: las cuotas son una tabla.
      width={720}
      title="Fiserv"
      load={() => getFiserv(sellerCode)}
      save={(values) => upsertFiserv(sellerCode, values)}
      savedMessage="Fiserv guardado"
      after={
        <>
          <Divider />
          {/* Las cuotas no son parte del formulario: cada una se guarda al confirmarla. */}
          <Subtitulo>Cuotas</Subtitulo>
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            Cada cuota se guarda al confirmarla, sin el botón Guardar.
          </Text>
          <FiservInstallments sellerCode={sellerCode} cards={cards} />
        </>
      }
    >
      <CamposComunes />
      <CampoModo />
      <Credenciales
        titulo="Credenciales de producción"
        produccion
        prefix="production"
      />
      <Credenciales
        titulo="Credenciales de prueba"
        produccion={false}
        prefix="integration"
      />
      <CampoMontoMaximo />

      <Subtitulo>Tarjetas</Subtitulo>
      <Form.Item
        label="Tarjetas aceptadas"
        name="cards"
        extra="Las que ve el cliente en el checkout. Vacío = las de la configuración general."
      >
        <Select
          mode="multiple"
          allowClear
          placeholder="Las de la configuración general"
          options={cards.map((c) => ({ value: c.code, label: c.label }))}
        />
      </Form.Item>
    </GatewayPanel>
  );
};
